import { z } from 'zod';
const claimType = z.enum(['Return', 'Warranty']);
const inOperator = (dimension) => z.strictObject({
    operator: z.literal('in'),
    /** the dataset's own dimension whose values the filter selects among */
    dimension,
});
const equalsOperator = z.strictObject({ operator: z.literal('equals') });
const claimTypesOperator = z.strictObject({
    operator: z.literal('in'),
    values: z.array(claimType),
});
const limits = {
    maxRows: 500,
    // twelve months back through the end of today: a leap year, today, and a daylight saving hour
    maxRangeDays: 368,
    maxBucketEdges: 400,
    maxDimensions: 3,
    maxFilterValues: 50,
};
// ClickHouse's DateTime64 holds 1900 through 2299; an instant outside it fails to parse or saturates silently
const instantFloor = Date.UTC(1900, 0, 1);
const instantCeiling = Date.UTC(2300, 0, 1);
const queryInstant = z.iso.datetime().refine((value) => {
    const instant = Date.parse(value);
    return instant >= instantFloor && instant < instantCeiling;
}, { message: 'Instants must fall in the years 1900 through 2299' });
const selectedValues = z
    .array(z.string().min(1).max(200))
    .min(1)
    .max(limits.maxFilterValues);
const selectedClaimTypes = z
    .array(claimType)
    .min(1)
    .max(claimType.options.length);
// the line tables carry no product id, so a product is matched on its title until they do
const productTitlesFilter = {
    operator: 'in',
    dimension: 'productTitle',
};
const catalogMetric = z.strictObject({
    description: z.string(),
    output: z.enum(['integer', 'decimalString']),
    nullable: z.boolean(),
    /** whether values add up across groups and buckets: a `distinct` count can overlap between them, and a `ratio` is a mean or rate */
    additive: z.enum(['sum', 'distinct', 'ratio']),
});
/** One dataset's catalog and request schemas, built from the fields it allows. */
const datasetSchemas = (dataset, fields) => ({
    catalog: z.strictObject({
        dataset: z.literal(dataset),
        dimensions: z.array(fields.dimension),
        metrics: z.record(fields.metric, catalogMetric),
        filters: fields.catalogFilters,
        timeFields: z.array(fields.timeField),
    }),
    request: z.strictObject({
        dataset: z.literal(dataset),
        metrics: z.array(fields.metric).min(1).max(fields.metric.options.length),
        dimensions: z.array(fields.dimension).max(limits.maxDimensions),
        // UTC only: the client picks bucket edges (e.g. its local midnights) and sends
        // them as UTC instants, so the API never needs a timezone
        time: z.strictObject({
            field: fields.timeField,
            from: queryInstant,
            to: queryInstant,
            /** empty for a single total over the range */
            bucketEdges: z.array(queryInstant).max(limits.maxBucketEdges),
        }),
        filters: fields.filters,
        orderBy: z
            .array(z.strictObject({
            field: z.union([
                z.literal('bucketStart'),
                fields.dimension,
                fields.metric,
            ]),
            direction: z.enum(['asc', 'desc']),
        }))
            .min(1)
            .max(1 + fields.dimension.options.length + fields.metric.options.length),
        /** a longer result is cut to its first `limit` rows in `orderBy` order, and the response says so in `truncated` */
        limit: z.int().min(1).max(limits.maxRows),
    }),
});
const claimItemDimension = z.enum([
    'reason',
    'reasonDetail',
    'reasonCategory',
    'status',
    'claimStatus',
    'resolution',
    'requestedResolution',
    'productType',
    'inspectionGrade',
    'productTitle',
    'variantTitle',
    'sku',
]);
export const claimItemMetric = z.enum([
    'claims',
    'lineItems',
    'items',
    'itemsPerClaim',
    'refundDeflectionRate',
    'refundSaveRate',
    'resolvedExternally',
    'requestedAmount',
    'resolvedAmount',
    'itemValue',
    'itemCost',
    'labelCost',
    'avgLabelCost',
    'avgDaysOrderToClaim',
    'avgDaysClaimToTransit',
    'avgDaysInTransit',
    'avgDaysDeliveredToResolved',
    'avgDaysClaimToResolved',
]);
// the Corso dashboards' names for each metric; the MCP data guide uses them too
export const claimItemMetricLabel = {
    claims: 'Requests',
    lineItems: 'Line items',
    items: 'Items',
    itemsPerClaim: 'Items per request',
    refundDeflectionRate: 'Resolved without a refund',
    refundSaveRate: 'Refund requests saved',
    resolvedExternally: 'Resolved externally',
    requestedAmount: 'Requested amount',
    resolvedAmount: 'Resolved amount',
    itemValue: 'Item value',
    itemCost: 'Item cost',
    labelCost: 'Label cost',
    avgLabelCost: 'Average label cost',
    avgDaysOrderToClaim: 'Days from order to claim',
    avgDaysClaimToTransit: 'Days from claim to dropoff',
    avgDaysInTransit: 'Days in transit',
    avgDaysDeliveredToResolved: 'Days from delivered to resolved',
    avgDaysClaimToResolved: 'Days from claim to resolved',
};
const claimItemTimeField = z.enum([
    'createdOn',
    'finalizedOn',
    'deliveredOn',
    'inTransitOn',
    'inspectionCreatedOn',
]);
const claimItems = datasetSchemas('claimItems', {
    dimension: claimItemDimension,
    metric: claimItemMetric,
    timeField: claimItemTimeField,
    filters: z.strictObject({
        claimTypes: selectedClaimTypes.optional(),
        reasons: selectedValues.optional(),
        statuses: selectedValues.optional(),
        claimStatuses: selectedValues.optional(),
        resolutions: selectedValues.optional(),
        requestedResolutions: selectedValues.optional(),
        productTypes: selectedValues.optional(),
        productTitles: selectedValues.optional(),
        hasInspection: z.boolean().optional(),
        /** `true` keeps rows whose resolution differs from the one requested; rows missing either are left out both ways */
        wasResolutionChanged: z.boolean().optional(),
        isShopNow: z.boolean().optional(),
    }),
    catalogFilters: z.strictObject({
        claimTypes: claimTypesOperator,
        reasons: inOperator(claimItemDimension),
        statuses: inOperator(claimItemDimension),
        claimStatuses: inOperator(claimItemDimension),
        resolutions: inOperator(claimItemDimension),
        requestedResolutions: inOperator(claimItemDimension),
        productTypes: inOperator(claimItemDimension),
        productTitles: inOperator(claimItemDimension),
        hasInspection: equalsOperator,
        wasResolutionChanged: equalsOperator,
        isShopNow: equalsOperator,
    }),
});
const averageDays = (from, to) => ({
    description: `Mean UTC calendar days from ${from} to ${to}, per line item with both dates, rounded to two places; null when none have both.`,
    output: 'decimalString',
    nullable: true,
    additive: 'ratio',
});
const claimItemsCatalog = {
    dataset: 'claimItems',
    dimensions: claimItemDimension.options,
    metrics: {
        claims: {
            description: 'Distinct non-null claim IDs; counts across reasons may overlap.',
            output: 'integer',
            nullable: false,
            additive: 'distinct',
        },
        lineItems: {
            description: 'Claim line items, counted once each whatever the quantity.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        items: {
            description: 'Sum of non-null quantities; null when none are present.',
            output: 'integer',
            nullable: true,
            additive: 'sum',
        },
        itemsPerClaim: {
            description: '`items` divided by distinct non-null claim IDs, rounded to two places; null when there are no claims.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        refundDeflectionRate: {
            description: 'Quantity resolved by any method but `Refund`, divided by the quantity with a resolution; a fraction rounded to four places, null when nothing is resolved.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        refundSaveRate: {
            description: 'Of the quantity requested as a `Refund` and resolved, the share resolved by another method; a fraction rounded to four places, null when no such item is resolved.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        resolvedExternally: {
            description: 'Claim line items flagged as resolved outside Corso.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        requestedAmount: {
            description: 'Sum of non-null requested amounts in source currency units, without conversion; null when none are present.',
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        resolvedAmount: {
            description: 'Sum of non-null resolved amounts in source currency units, without conversion; null when none are present.',
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        itemValue: {
            description: 'Sum of original order line item unit price times quantity, where both are present, in source currency units, without conversion; null when none are.',
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        itemCost: {
            description: "Sum of the variant's current unit cost times quantity, where both are present, in source currency units, without conversion; null when none are.",
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        labelCost: {
            description: 'Return label cost, counting each distinct rate once per claim because a label covering several line items carries its whole rate on each; in the currency the label was billed in, usually the store’s, and never converted; zero when none are present.',
            output: 'decimalString',
            nullable: false,
            additive: 'distinct',
        },
        avgLabelCost: {
            description: 'Mean return label cost per distinct rate, counted once per claim as in `labelCost`, rounded to four places; null when none are present.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        avgDaysOrderToClaim: averageDays('the original order', 'the claim'),
        avgDaysClaimToTransit: averageDays('the claim', 'the return going in transit'),
        avgDaysInTransit: averageDays('the return going in transit', 'its delivery'),
        avgDaysDeliveredToResolved: averageDays('the return delivery', 'the claim being finalized, counting a claim finalized before delivery as zero'),
        avgDaysClaimToResolved: averageDays('the claim', 'it being finalized'),
    },
    filters: {
        claimTypes: { operator: 'in', values: claimType.options },
        reasons: { operator: 'in', dimension: 'reason' },
        statuses: { operator: 'in', dimension: 'status' },
        claimStatuses: { operator: 'in', dimension: 'claimStatus' },
        resolutions: { operator: 'in', dimension: 'resolution' },
        requestedResolutions: { operator: 'in', dimension: 'requestedResolution' },
        productTypes: { operator: 'in', dimension: 'productType' },
        productTitles: productTitlesFilter,
        hasInspection: { operator: 'equals' },
        wasResolutionChanged: { operator: 'equals' },
        isShopNow: { operator: 'equals' },
    },
    timeFields: claimItemTimeField.options,
};
const orderItemMetric = z.enum([
    'itemsSold',
    'returnItemsFinalized',
    'warrantyItemsFinalized',
    'returnRate',
    'warrantyRate',
]);
const orderItemDimension = z.enum([
    'productTitle',
    'variantTitle',
    'lineItemName',
]);
const orderItemTimeField = z.enum(['createdOn']);
const orderItems = datasetSchemas('orderItems', {
    dimension: orderItemDimension,
    metric: orderItemMetric,
    timeField: orderItemTimeField,
    filters: z.strictObject({ productTitles: selectedValues.optional() }),
    catalogFilters: z.strictObject({
        productTitles: inOperator(orderItemDimension),
    }),
});
const finalizedItems = (claims) => ({
    description: `Units on ${claims} claim line items that were approved or denied, counted on the order line they were sold on; null when no order lines match.`,
    output: 'integer',
    nullable: true,
    additive: 'sum',
});
const finalizedRate = (claims, items) => ({
    description: `\`${items}\` divided by \`itemsSold\`, so it counts denied ${claims} items as well as approved ones; a fraction rounded to four places, null when nothing was sold.`,
    output: 'decimalString',
    nullable: true,
    additive: 'ratio',
});
const orderItemsCatalog = {
    dataset: 'orderItems',
    dimensions: orderItemDimension.options,
    metrics: {
        itemsSold: {
            description: "Sum of non-null order line quantities, with canceled lines and Corso's own shipping protection line included; null when none are present.",
            output: 'integer',
            nullable: true,
            additive: 'sum',
        },
        returnItemsFinalized: finalizedItems('return'),
        warrantyItemsFinalized: finalizedItems('warranty'),
        returnRate: finalizedRate('return', 'returnItemsFinalized'),
        warrantyRate: finalizedRate('warranty', 'warrantyItemsFinalized'),
    },
    filters: { productTitles: productTitlesFilter },
    timeFields: orderItemTimeField.options,
};
const claimDimension = z.enum(['status', 'creditResolution']);
const claimMetric = z.enum([
    'claims',
    'newRevenue',
    'retainedRevenue',
    'feesApplied',
    'returnShippingCharged',
    'giftCardIncentives',
    'shopNowIncentives',
    'totalRevenue',
    'totalFees',
    'totalIncentives',
]);
const claimTimeField = z.enum(['createdOn', 'finalizedOn']);
const claims = datasetSchemas('claims', {
    dimension: claimDimension,
    metric: claimMetric,
    timeField: claimTimeField,
    filters: z.strictObject({
        claimTypes: selectedClaimTypes.optional(),
        statuses: selectedValues.optional(),
        hasShopNow: z.boolean().optional(),
    }),
    catalogFilters: z.strictObject({
        claimTypes: claimTypesOperator,
        statuses: inOperator(claimDimension),
        hasShopNow: equalsOperator,
    }),
});
const claimAmount = (amount) => ({
    description: `Sum of each claim's ${amount}, where present, in source currency units, without conversion; null when none are.`,
    output: 'decimalString',
    nullable: true,
    additive: 'sum',
});
const combinedAmount = (first, second) => ({
    description: `\`${first}\` plus \`${second}\`, with a null one counted as zero; null when both are.`,
    output: 'decimalString',
    nullable: true,
    additive: 'sum',
});
const claimsCatalog = {
    dataset: 'claims',
    dimensions: claimDimension.options,
    metrics: {
        claims: {
            description: 'Requests, one per claim.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        newRevenue: claimAmount('new revenue, such as what an exchange order added above the original item value'),
        retainedRevenue: claimAmount('retained revenue, kept through an exchange or Shop Now and not refunded'),
        feesApplied: claimAmount('service fee charged to the customer'),
        returnShippingCharged: claimAmount('return shipping charged to the customer'),
        giftCardIncentives: claimAmount('gift card incentive'),
        shopNowIncentives: claimAmount('Shop Now incentive'),
        totalRevenue: combinedAmount('newRevenue', 'retainedRevenue'),
        totalFees: combinedAmount('feesApplied', 'returnShippingCharged'),
        totalIncentives: combinedAmount('giftCardIncentives', 'shopNowIncentives'),
    },
    filters: {
        claimTypes: { operator: 'in', values: claimType.options },
        statuses: { operator: 'in', dimension: 'status' },
        hasShopNow: { operator: 'equals' },
    },
    timeFields: claimTimeField.options,
};
const orderDimension = z.enum([
    'salesChannel',
    'shippingTitle',
    'carrier',
    'orderProtectionPrice',
]);
const orderMetric = z.enum([
    'orders',
    'protectedOrders',
    'attachRate',
    'protectionPrice',
    'avgProtectionPrice',
    'shippingCharged',
    'avgShippingCharge',
    'carbonGrams',
]);
const orderTimeField = z.enum(['orderedOn']);
const orders = datasetSchemas('orders', {
    dimension: orderDimension,
    metric: orderMetric,
    timeField: orderTimeField,
    filters: z.strictObject({
        salesChannels: selectedValues.optional(),
        /** `false` keeps orders without shipping protection, whose flag is null in the source */
        isProtected: z.boolean().optional(),
        /** Placed through a storefront channel; `false` keeps every other order, those with no channel included */
        isOnline: z.boolean().optional(),
    }),
    catalogFilters: z.strictObject({
        salesChannels: inOperator(orderDimension),
        isProtected: equalsOperator,
        isOnline: equalsOperator,
    }),
});
const averageAmount = (amount, rows) => ({
    description: `Mean ${amount} over the ${rows} that have one, rounded to four places; null when none do.`,
    output: 'decimalString',
    nullable: true,
    additive: 'ratio',
});
const ordersCatalog = {
    dataset: 'orders',
    dimensions: orderDimension.options,
    metrics: {
        orders: {
            description: 'Orders, canceled ones included.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        protectedOrders: {
            description: 'Orders with shipping protection attached.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        attachRate: {
            description: '`protectedOrders` divided by `orders`; a fraction rounded to four places, null when there are no orders.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        protectionPrice: {
            description: "Sum of each protected order's protection tier price, in USD at the tier's current price, which is not the amount charged at checkout; null when none are present.",
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        avgProtectionPrice: {
            description: '`protectionPrice` divided by `protectedOrders`, in USD, rounded to four places; null when no order is protected.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        shippingCharged: {
            description: 'Sum of the shipping price charged on each order, where present, in source currency units, without conversion; null when none are.',
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        avgShippingCharge: averageAmount('shipping price', 'orders'),
        carbonGrams: {
            description: 'Sum of the grams of carbon recorded on each order; null when none are present.',
            output: 'integer',
            nullable: true,
            additive: 'sum',
        },
    },
    filters: {
        salesChannels: { operator: 'in', dimension: 'salesChannel' },
        isProtected: { operator: 'equals' },
        isOnline: { operator: 'equals' },
    },
    timeFields: orderTimeField.options,
};
const shippingClaimDimension = z.enum(['reason', 'status', 'source']);
const shippingClaimMetric = z.enum([
    'claims',
    'refundAmount',
    'reorderAmount',
    'totalClaimAmount',
    'avgRefundAmount',
    'avgReorderAmount',
]);
const shippingClaimTimeField = z.enum(['createdOn', 'resolvedOn']);
const shippingClaims = datasetSchemas('shippingClaims', {
    dimension: shippingClaimDimension,
    metric: shippingClaimMetric,
    timeField: shippingClaimTimeField,
    filters: z.strictObject({ statuses: selectedValues.optional() }),
    catalogFilters: z.strictObject({
        statuses: inOperator(shippingClaimDimension),
    }),
});
const shippingClaimsCatalog = {
    dataset: 'shippingClaims',
    dimensions: shippingClaimDimension.options,
    metrics: {
        claims: {
            description: 'Shipping protection claims.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        refundAmount: {
            description: "Sum of each claim's refunds, where it has any, in source currency units, without conversion; null when none do.",
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        reorderAmount: {
            description: "Sum of each claim's reorders, where it has any, at the reorder's recorded amount or else its order total, in source currency units, without conversion; null when none do.",
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
        totalClaimAmount: combinedAmount('refundAmount', 'reorderAmount'),
        avgRefundAmount: averageAmount('refund total', 'claims'),
        avgReorderAmount: averageAmount('reorder total', 'claims'),
    },
    filters: { statuses: { operator: 'in', dimension: 'status' } },
    timeFields: shippingClaimTimeField.options,
};
const registrationDimension = z.enum([
    'salesChannelName',
    'productTitle',
    'variantDisplayName',
]);
const registrationMetric = z.enum([
    'registrations',
    'registeredProducts',
    'orderClaims',
    'newCustomers',
    'newSubscribers',
]);
const registrationTimeField = z.enum(['createdOn', 'purchasedOn']);
const registrations = datasetSchemas('registrations', {
    dimension: registrationDimension,
    metric: registrationMetric,
    timeField: registrationTimeField,
    filters: z.strictObject({
        salesChannelNames: selectedValues.optional(),
        productTitles: selectedValues.optional(),
    }),
    catalogFilters: z.strictObject({
        salesChannelNames: inOperator(registrationDimension),
        productTitles: inOperator(registrationDimension),
    }),
});
const registrationsCatalog = {
    dataset: 'registrations',
    dimensions: registrationDimension.options,
    metrics: {
        registrations: {
            description: 'Distinct registrations, canceled ones included; one covering several products counts in each product group.',
            output: 'integer',
            nullable: false,
            additive: 'distinct',
        },
        registeredProducts: {
            description: "Line items on the registrations' orders, one each whatever the quantity; a registration whose order has no line items still counts once, under a null product.",
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        orderClaims: {
            description: "Claims on the registrations' orders, of any type and not only warranties, counting each order's claims once; zero when none are present. The count is as of each registration's last update, so a claim filed after it may be missing.",
            output: 'integer',
            nullable: false,
            additive: 'distinct',
        },
        newCustomers: {
            description: 'Distinct registrations that created their customer on the platform, which had no customer with that email before.',
            output: 'integer',
            nullable: false,
            additive: 'distinct',
        },
        newSubscribers: {
            description: 'Distinct registrations that subscribed their customer to email marketing on the platform, where the customer was not subscribed before.',
            output: 'integer',
            nullable: false,
            additive: 'distinct',
        },
    },
    filters: {
        salesChannelNames: { operator: 'in', dimension: 'salesChannelName' },
        productTitles: productTitlesFilter,
    },
    timeFields: registrationTimeField.options,
};
const orderEditDimension = z.enum(['editType', 'orderId']);
const orderEditMetric = z.enum(['edits', 'minutesSaved', 'newRevenue']);
const orderEditTimeField = z.enum(['createdOn']);
const orderEdits = datasetSchemas('orderEdits', {
    dimension: orderEditDimension,
    metric: orderEditMetric,
    timeField: orderEditTimeField,
    filters: z.strictObject({}),
    catalogFilters: z.strictObject({}),
});
const orderEditsCatalog = {
    dataset: 'orderEdits',
    dimensions: orderEditDimension.options,
    metrics: {
        edits: {
            description: 'Order edits applied.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        minutesSaved: {
            description: "Sum of each edit's estimated minutes saved, where it has one; null when none do.",
            output: 'integer',
            nullable: true,
            additive: 'sum',
        },
        newRevenue: {
            description: "Sum of each edit's new revenue, where it has any, in source currency units, without conversion; null when none do.",
            output: 'decimalString',
            nullable: true,
            additive: 'sum',
        },
    },
    filters: {},
    timeFields: orderEditTimeField.options,
};
const supportConversationMetric = z.enum([
    'conversations',
    'avgFirstResponseMinutes',
]);
const supportConversationTimeField = z.enum(['startedOn', 'firstRespondedOn']);
const supportConversations = datasetSchemas('supportConversations', {
    dimension: z.enum([]),
    metric: supportConversationMetric,
    timeField: supportConversationTimeField,
    filters: z.strictObject({}),
    catalogFilters: z.strictObject({}),
});
const supportConversationsCatalog = {
    dataset: 'supportConversations',
    dimensions: [],
    metrics: {
        conversations: {
            description: 'Conversations Corso Concierge handled, counted as Front counts them: a conversation idle for 5 days starts a new one at its next activity.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        avgFirstResponseMinutes: {
            description: "Mean minutes from a conversation's first customer message to the first teammate reply, inside Front's business hours (Monday to Friday 9:00-17:00 Mountain time), to 2 places; null when none were answered.",
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
    },
    filters: {},
    timeFields: supportConversationTimeField.options,
};
const supportMessageDimension = z.enum(['sentBy', 'channel']);
const supportMessageMetric = z.enum(['messagesSent', 'messagesReceived']);
const supportMessageTimeField = z.enum(['sentOn']);
const supportMessages = datasetSchemas('supportMessages', {
    dimension: supportMessageDimension,
    metric: supportMessageMetric,
    timeField: supportMessageTimeField,
    filters: z.strictObject({}),
    catalogFilters: z.strictObject({}),
});
const supportMessagesCatalog = {
    dataset: 'supportMessages',
    dimensions: supportMessageDimension.options,
    metrics: {
        messagesSent: {
            description: 'Messages sent to customers, by teammates or by automation such as autoreplies, as Front counts them.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        messagesReceived: {
            description: 'Messages customers sent.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
    },
    filters: {},
    timeFields: supportMessageTimeField.options,
};
const supportCsatDimension = z.enum(['csatRating']);
const supportCsatMetric = z.enum([
    'csatScore',
    'scoresCollected',
    'scoresWithComment',
]);
const supportCsatTimeField = z.enum(['ratedOn']);
const supportCsat = datasetSchemas('supportCsat', {
    dimension: supportCsatDimension,
    metric: supportCsatMetric,
    timeField: supportCsatTimeField,
    filters: z.strictObject({}),
    catalogFilters: z.strictObject({}),
});
const supportCsatCatalog = {
    dataset: 'supportCsat',
    dimensions: supportCsatDimension.options,
    metrics: {
        csatScore: {
            description: 'Mean of each survey rating as a fraction of its scale, to 4 places; null when no ratings were collected.',
            output: 'decimalString',
            nullable: true,
            additive: 'ratio',
        },
        scoresCollected: {
            description: 'Customer satisfaction survey ratings collected.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
        scoresWithComment: {
            description: 'Ratings that came with a written comment.',
            output: 'integer',
            nullable: false,
            additive: 'sum',
        },
    },
    filters: {},
    timeFields: supportCsatTimeField.options,
};
const group = z.string().nullable().optional();
const count = z.int().nonnegative().optional();
const sum = z.int().nullable().optional();
const amount = z
    .string()
    .regex(/^-?\d+(?:\.\d+)?$/)
    .nullable()
    .optional();
// the last row of a page; the next page holds the rows sorted after it
const listCursor = z.strictObject({
    time: queryInstant,
    id: z.int(),
});
const text = z.string().nullable();
const instant = z.iso.datetime().nullable();
const wholeNumber = z.int().nullable();
const decimal = amount.unwrap();
/** The row-level list of a dataset: its catalog, request and response schemas, built from the row it returns. */
const listSchemas = (list, dataset, 
// a row carries every date it can be listed by, so its own time can be handed back as the cursor
row) => {
    const request = z.strictObject({
        list: z.literal(list),
        // the dataset's time fields and UTC range; rows come back newest first on this field, then by `id`
        time: z.strictObject({
            field: dataset.request.shape.time.shape.field,
            from: queryInstant,
            to: queryInstant,
        }),
        filters: dataset.request.shape.filters,
        /** null for the first page, then the previous page's `nextCursor` */
        cursor: listCursor.nullable(),
        limit: z.int().min(1).max(limits.maxRows),
    });
    return {
        catalog: z.strictObject({
            list: z.literal(list),
            columns: z.record(row.keyof(), z.strictObject({
                description: z.string(),
                output: z.enum(['integer', 'decimalString', 'string', 'datetime']),
                nullable: z.boolean(),
            })),
        }),
        request,
        response: z.strictObject({
            query: request,
            rows: z.array(row),
            /** null on the last page */
            nextCursor: listCursor.nullable(),
        }),
    };
};
const claimItemListRow = z.strictObject({
    id: z.int(),
    createdOn: instant,
    finalizedOn: instant,
    deliveredOn: instant,
    inTransitOn: instant,
    inspectionCreatedOn: instant,
    orderedOn: instant,
    orderNumber: text,
    productTitle: text,
    sku: text,
    quantity: wholeNumber,
    reason: text,
    resolution: text,
    inspectionGrade: text,
});
const claimItemList = listSchemas('claimItems', claimItems, claimItemListRow);
const claimItemListCatalog = {
    list: 'claimItems',
    columns: {
        id: {
            description: 'Claim line item ID.',
            output: 'integer',
            nullable: false,
        },
        createdOn: {
            description: 'When the claim line item was created.',
            output: 'datetime',
            nullable: true,
        },
        finalizedOn: {
            description: 'When the claim was finalized.',
            output: 'datetime',
            nullable: true,
        },
        deliveredOn: {
            description: 'When the return shipment was delivered.',
            output: 'datetime',
            nullable: true,
        },
        inTransitOn: {
            description: 'When the return shipment went in transit.',
            output: 'datetime',
            nullable: true,
        },
        inspectionCreatedOn: {
            description: 'When the item was inspected.',
            output: 'datetime',
            nullable: true,
        },
        orderedOn: {
            description: 'When the original order was placed.',
            output: 'datetime',
            nullable: true,
        },
        orderNumber: {
            description: 'Order number of the original order.',
            output: 'string',
            nullable: true,
        },
        productTitle: {
            description: 'Product title.',
            output: 'string',
            nullable: true,
        },
        sku: {
            description: 'SKU on the original order line.',
            output: 'string',
            nullable: true,
        },
        quantity: {
            description: 'Units on the claim line item.',
            output: 'integer',
            nullable: true,
        },
        reason: {
            description: 'Claim line item reason.',
            output: 'string',
            nullable: true,
        },
        resolution: {
            description: 'Resolution method.',
            output: 'string',
            nullable: true,
        },
        inspectionGrade: {
            description: 'Inspection grade; null when the item has no inspection or its inspection has no grade.',
            output: 'string',
            nullable: true,
        },
    },
};
const orderListRow = z.strictObject({
    id: z.int(),
    orderedOn: instant,
    orderNumber: text,
    salesChannel: text,
    orderTotal: decimal,
    protectionPrice: decimal,
});
const orderList = listSchemas('orders', orders, orderListRow);
const orderListCatalog = {
    list: 'orders',
    columns: {
        id: {
            description: 'Corso order ID.',
            output: 'integer',
            nullable: false,
        },
        orderedOn: {
            description: 'When the order was placed.',
            output: 'datetime',
            nullable: true,
        },
        orderNumber: {
            description: 'Order number.',
            output: 'string',
            nullable: true,
        },
        salesChannel: {
            description: 'Sales channel.',
            output: 'string',
            nullable: true,
        },
        orderTotal: {
            description: 'Order total in source currency units, without conversion.',
            output: 'decimalString',
            nullable: true,
        },
        protectionPrice: {
            description: "The order's protection tier price, in USD at the tier's current price, which is not the amount charged at checkout; null when the order is not protected.",
            output: 'decimalString',
            nullable: true,
        },
    },
};
const shippingClaimListRow = z.strictObject({
    id: z.int(),
    createdOn: instant,
    resolvedOn: instant,
    orderName: text,
    status: text,
    resolution: text,
    reason: text,
});
const shippingClaimList = listSchemas('shippingClaims', shippingClaims, shippingClaimListRow);
const shippingClaimListCatalog = {
    list: 'shippingClaims',
    columns: {
        id: {
            description: 'Shipping protection claim ID.',
            output: 'integer',
            nullable: false,
        },
        createdOn: {
            description: 'When the claim was submitted.',
            output: 'datetime',
            nullable: true,
        },
        resolvedOn: {
            description: 'When the claim was resolved.',
            output: 'datetime',
            nullable: true,
        },
        orderName: {
            description: "The original order's name on the platform, such as #1001: its number as the customer sees it.",
            output: 'string',
            nullable: true,
        },
        status: {
            description: 'Claim status.',
            output: 'string',
            nullable: true,
        },
        resolution: {
            description: 'Resolution method.',
            output: 'string',
            nullable: true,
        },
        reason: {
            description: 'Claim reason.',
            output: 'string',
            nullable: true,
        },
    },
};
export const analyticsCatalogResponse = z.strictObject({
    datasets: z.tuple([
        claimItems.catalog,
        orderItems.catalog,
        claims.catalog,
        orders.catalog,
        shippingClaims.catalog,
        registrations.catalog,
        orderEdits.catalog,
        supportConversations.catalog,
        supportMessages.catalog,
        supportCsat.catalog,
    ]),
    /** row-level lists: each is named for a dataset, takes its time fields and filters, and returns these columns */
    lists: z.tuple([
        claimItemList.catalog,
        orderList.catalog,
        shippingClaimList.catalog,
    ]),
    time: z.strictObject({
        timezone: z.literal('UTC'),
        bounds: z.literal('[from,to)'),
        bucketEdges: z.literal('ascending, first equals from, all before to'),
    }),
    limits: z.strictObject({
        maxRows: z.int().positive(),
        maxRangeDays: z.int().positive(),
        maxBucketEdges: z.int().positive(),
        maxDimensions: z.int().positive(),
        maxFilterValues: z.int().positive(),
    }),
});
export const analyticsCatalog = {
    datasets: [
        claimItemsCatalog,
        orderItemsCatalog,
        claimsCatalog,
        ordersCatalog,
        shippingClaimsCatalog,
        registrationsCatalog,
        orderEditsCatalog,
        supportConversationsCatalog,
        supportMessagesCatalog,
        supportCsatCatalog,
    ],
    lists: [claimItemListCatalog, orderListCatalog, shippingClaimListCatalog],
    time: {
        timezone: 'UTC',
        bounds: '[from,to)',
        bucketEdges: 'ascending, first equals from, all before to',
    },
    limits,
};
const rangeMessage = `Date range must be between 1 and ${limits.maxRangeDays} days`;
const isRangeAllowed = ({ from, to }) => {
    const rangeDays = (Date.parse(to) - Date.parse(from)) / 86_400_000;
    return rangeDays > 0 && rangeDays <= limits.maxRangeDays;
};
export const claimItemsQueryRequest = claimItems.request;
export const analyticsQueryRequest = z
    .discriminatedUnion('dataset', [
    claimItems.request,
    orderItems.request,
    claims.request,
    orders.request,
    shippingClaims.request,
    registrations.request,
    orderEdits.request,
    supportConversations.request,
    supportMessages.request,
    supportCsat.request,
])
    .superRefine((request, context) => {
    if (!isRangeAllowed(request.time)) {
        context.addIssue({
            code: 'custom',
            message: rangeMessage,
            path: ['time', 'to'],
        });
    }
    for (const key of ['dimensions', 'metrics']) {
        if (new Set(request[key]).size !== request[key].length) {
            context.addIssue({
                code: 'custom',
                message: `${key} must be unique`,
                path: [key],
            });
        }
    }
    const { from, to, bucketEdges } = request.time;
    const edges = bucketEdges.map((edge) => Date.parse(edge));
    const isAscending = edges
        .slice(1)
        .every((edge, index) => edge > (edges[index] ?? edge));
    if (edges.length > 0 &&
        (edges[0] !== Date.parse(from) ||
            !isAscending ||
            edges.some((edge) => edge >= Date.parse(to)) ||
            // bucketStart comes back in whole seconds, so a fractional edge would never match its rows
            edges.some((edge) => edge % 1000 !== 0))) {
        context.addIssue({
            code: 'custom',
            message: 'Bucket edges must be whole seconds, ascend, start at `from`, and fall before `to`',
            path: ['time', 'bucketEdges'],
        });
    }
    const groups = [
        ...request.dimensions,
        ...(bucketEdges.length === 0 ? [] : ['bucketStart']),
    ];
    const selected = new Set([...groups, ...request.metrics]);
    const ordered = new Set(request.orderBy.map(({ field }) => field));
    if (ordered.size !== request.orderBy.length ||
        [...ordered].some((field) => !selected.has(field)) ||
        groups.some((field) => !ordered.has(field))) {
        context.addIssue({
            code: 'custom',
            message: 'Order fields must be unique, selected, and include every grouping field for stable results',
            path: ['orderBy'],
        });
    }
});
const analyticsRow = z.strictObject({
    bucketStart: z.iso.datetime().optional(),
    reason: group,
    reasonDetail: group,
    reasonCategory: group,
    status: group,
    claimStatus: group,
    resolution: group,
    requestedResolution: group,
    productType: group,
    inspectionGrade: group,
    productTitle: group,
    variantTitle: group,
    sku: group,
    lineItemName: group,
    salesChannel: group,
    shippingTitle: group,
    carrier: group,
    salesChannelName: group,
    variantDisplayName: group,
    // a decimal string, unlike the other groups
    orderProtectionPrice: amount,
    source: group,
    editType: group,
    // Corso's own order ID as a string, like every other group
    orderId: group,
    creditResolution: group,
    sentBy: group,
    channel: group,
    csatRating: group,
    claims: count,
    lineItems: count,
    items: sum,
    itemsPerClaim: amount,
    refundDeflectionRate: amount,
    refundSaveRate: amount,
    resolvedExternally: count,
    requestedAmount: amount,
    resolvedAmount: amount,
    itemValue: amount,
    itemCost: amount,
    labelCost: amount,
    avgLabelCost: amount,
    avgDaysOrderToClaim: amount,
    avgDaysClaimToTransit: amount,
    avgDaysInTransit: amount,
    avgDaysDeliveredToResolved: amount,
    avgDaysClaimToResolved: amount,
    itemsSold: sum,
    returnItemsFinalized: sum,
    warrantyItemsFinalized: sum,
    returnRate: amount,
    warrantyRate: amount,
    newRevenue: amount,
    retainedRevenue: amount,
    feesApplied: amount,
    returnShippingCharged: amount,
    giftCardIncentives: amount,
    shopNowIncentives: amount,
    totalRevenue: amount,
    totalFees: amount,
    totalIncentives: amount,
    orders: count,
    protectedOrders: count,
    attachRate: amount,
    protectionPrice: amount,
    avgProtectionPrice: amount,
    shippingCharged: amount,
    avgShippingCharge: amount,
    carbonGrams: sum,
    refundAmount: amount,
    reorderAmount: amount,
    totalClaimAmount: amount,
    avgRefundAmount: amount,
    avgReorderAmount: amount,
    registrations: count,
    registeredProducts: count,
    orderClaims: count,
    newCustomers: count,
    newSubscribers: count,
    edits: count,
    minutesSaved: sum,
    conversations: count,
    avgFirstResponseMinutes: amount,
    messagesSent: count,
    messagesReceived: count,
    csatScore: amount,
    scoresCollected: count,
    scoresWithComment: count,
});
export const analyticsQueryResponse = z.strictObject({
    query: analyticsQueryRequest,
    rows: z.array(analyticsRow),
    /** `true` when the result had more rows than `limit`, so `rows` is only its first `limit` */
    truncated: z.boolean(),
});
export const analyticsListRequest = z
    .discriminatedUnion('list', [
    claimItemList.request,
    orderList.request,
    shippingClaimList.request,
])
    .refine(({ time }) => isRangeAllowed(time), {
    message: rangeMessage,
    path: ['time', 'to'],
});
export const analyticsListResponse = z.union([
    claimItemList.response,
    orderList.response,
    shippingClaimList.response,
]);
//# sourceMappingURL=analytics.js.map